import { injectScript } from '../utils/script';
import { getBrand } from '../../../core/components/OpenGraph/OpenGraphProductData';
import { Cookies } from 'react-cookie';
import { CONSENT_COOKIE_NAME, defaultConsentSettings } from '../AnalyticsProvider';
const ga4 = () => window.gtag;
function productToItem(product, selectedVariation, useArticleNumberAsId = false, brandCustomField, category) {
    var _a, _b, _c, _d;
    const item_categories = category
        .split('/')
        .filter(Boolean)
        .reduce((acc, c, i) => {
        if (i === 0) {
            acc.item_category = c;
        }
        else {
            acc[`item_category${i + 1}`] = c;
        }
        return acc;
    }, {});
    return Object.assign({ item_name: product === null || product === void 0 ? void 0 : product.name, item_id: useArticleNumberAsId
            ? product === null || product === void 0 ? void 0 : product.articleNumber
            : (_a = product === null || product === void 0 ? void 0 : product.id) === null || _a === void 0 ? void 0 : _a.toString(), price: ((_b = selectedVariation === null || selectedVariation === void 0 ? void 0 : selectedVariation.price) === null || _b === void 0 ? void 0 : _b.incVat) || ((_c = product === null || product === void 0 ? void 0 : product.price) === null || _c === void 0 ? void 0 : _c.incVat), item_variant: (_d = selectedVariation === null || selectedVariation === void 0 ? void 0 : selectedVariation.values) === null || _d === void 0 ? void 0 : _d.join('_'), item_brand: getBrand(product, brandCustomField) }, item_categories);
}
export function ga4Tracker({ trackingId, additionalGtagTrackingIds = [], currency, brandCustomField, useArticleNumberAsId = false, useGa4EventsFromGtm = false }) {
    return {
        name: 'ga4',
        initBrowser({ config }) {
            const w = window;
            w.dataLayer = w.dataLayer || [];
            w.dataLayer.push({
                'gtm.start': new Date().getTime(),
                event: 'gtm.js',
                originalLocation: document.location.protocol +
                    '//' +
                    document.location.hostname +
                    document.location.pathname +
                    document.location.search
            });
            w.gtag = function gtag() {
                w.dataLayer.push(arguments);
            };
            if (config.useTrackingConsentAPI) {
                const consents = config.cookies.get(CONSENT_COOKIE_NAME) || defaultConsentSettings;
                // NB: Only cookieless pings will be sent to Google servers. https://support.google.com/analytics/answer/9976101?hl=en
                // There’s also a wait time of 500 milliseconds before any Google tags are allowed to fire,
                // to give time for the update command to be invoked.
                ga4()('consent', 'default', Object.assign({}, consents));
                // NB: This only works when you set ad_storage to denied.
                // https://developers.google.com/tag-platform/devguides/consent#redact_ads_data
                ga4()('set', 'ads_data_redaction', true);
            }
            // We presume these are added from GTM
            if (!useGa4EventsFromGtm) {
                w.gtag('js', new Date());
                if (process.env.REACT_APP_DEBUG_MODE === 'true') {
                    ga4()('config', trackingId, {
                        debug_mode: true
                    });
                }
                else {
                    ga4()('config', trackingId);
                }
                additionalGtagTrackingIds.forEach((id) => {
                    ga4()('config', id);
                });
                injectScript({
                    src: `https://www.googletagmanager.com/gtag/js?id=${trackingId}`
                });
            }
            ga4();
        },
        consent(consents) {
            const cookies = new Cookies();
            // Fallback to all granted if no consents are passed
            const consent = consents
                ? consents
                : {
                    ad_storage: 'granted',
                    analytics_storage: 'granted',
                    personalization_storage: 'granted',
                    functionality_storage: 'granted',
                    security_storage: 'granted'
                };
            ga4()('consent', 'update', Object.assign({}, consent));
            cookies.set(CONSENT_COOKIE_NAME, JSON.stringify(Object.assign({}, consent)));
        },
        trackProduct({ quantity, action, product, selectedVariation, list, listName, index, category }, context, eventId) {
            const productItem = Object.assign(Object.assign({}, productToItem(product, selectedVariation, useArticleNumberAsId, brandCustomField, category)), { quantity, item_list_name: list || listName || context.list, index: index });
            switch (action) {
                case 'click':
                    ga4()('event', 'select_item', {
                        content_type: 'product',
                        currency,
                        value: product.price.incVat,
                        items: [productItem],
                        eventId
                    });
                    break;
                case 'view':
                    ga4()('event', 'view_item', {
                        currency,
                        value: product.price.incVat,
                        items: [productItem],
                        eventId
                    });
                    break;
                case 'add':
                    ga4()('event', 'add_to_cart', {
                        currency,
                        value: product.price.incVat,
                        items: [productItem],
                        eventId
                    });
                    break;
                case 'remove':
                    ga4()('event', 'remove_from_cart', {
                        currency,
                        value: product.price.incVat,
                        items: [productItem],
                        eventId
                    });
                    break;
                default:
                    console.log('Error: Gtag can not track action ' + action);
                    break;
            }
        },
        // This event is disabled in GA4 so that the event fires on the Checkout Page instead using tag manager
        // trackCartCheckout({ cart }, _context) {
        //   const items: GA4Item[] = cart.items.map((item) => ({
        //     ...productToItem(
        //       item.product as Product,
        //       item.variant,
        //       useArticleNumberAsId,
        //       brandCustomField
        //     ),
        //     quantity: item.quantity
        //   }));
        //   ga4()('event', 'begin_checkout', {
        //     currency: currency,
        //     value: cart.productTotal.incVat,
        //     items
        //   });
        // },
        trackPageView({ pathname, title }, _context, eventId) {
            ga4()('event', 'page_view', {
                currency,
                page_path: pathname,
                page_title: title,
                eventId
            });
        },
        trackList({ listName, products, category = '' }, _context, eventId) {
            const items = products.map((product, i) => {
                var _a;
                return (Object.assign(Object.assign({}, productToItem(product, null, useArticleNumberAsId, brandCustomField, category)), { price: (_a = product.price) === null || _a === void 0 ? void 0 : _a.incVat, item_list_name: listName, index: i }));
            });
            ga4()('event', 'view_item_list', {
                item_list_id: category,
                item_list_name: listName,
                items,
                eventId
            });
        },
        trackExperiment({ id, variant }, _context, eventId) {
            ga4()('config', trackingId, {
                experiments: [{ id, variant }],
                eventId
            });
        },
        trackUser({ userId }, _context, eventId) {
            ga4()('event', 'authentication', { user_id: userId, eventId });
        },
        trackLogin(_props, _context, eventId) {
            ga4()('event', 'login', { trackingId, eventId });
        },
        trackRegister(_props, _context, eventId) {
            ga4()('event', 'sign_up', { trackingId, eventId });
        },
        trackAddToWishList({ articleNumber, productName }, _context, eventId) {
            ga4()('event', 'add_to_wishlist', {
                articleNumber,
                productName,
                eventId
            });
        }
    };
}
//# sourceMappingURL=ga4.js.map